import { isEmpty } from 'lodash';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Header from 'layout/atoms/Header/Header';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { addSizeStandardSizeName, fetchSizes, removeSizeStandardSizeName, resetSizes, selectSizeById, updateSizeStandardName, updateSizeStandardSizeName, } from 'store/parameters/sizesSlice';
function SizeEditor() {
    var _a;
    const { t } = useTranslation('specification');
    const dispatch = useDispatch();
    const sizeId = (_a = useSelector(selectModalByName('sizeEditor'))) === null || _a === void 0 ? void 0 : _a.id;
    const data = useSelector((state) => selectSizeById(state, sizeId || '')) || null;
    const [sizeNameInput, setSizeNameInput] = useState('');
    const [sizeStandard, setSizeStandard] = useState(data === null || data === void 0 ? void 0 : data.name);
    const [sizeNames, setSizeNames] = useState(data === null || data === void 0 ? void 0 : data.sizes.map((size) => ({
        id: size.id,
        name: size.name,
        canNameBeUpdated: size === null || size === void 0 ? void 0 : size.canNameBeUpdated,
        canBeRemoved: size === null || size === void 0 ? void 0 : size.canBeRemoved,
        error: false,
        errorMessage: '',
    })));
    const MAX_SIZES_COUNT = 7;
    const sizeNameInputError = sizeNames.some((sizeName) => sizeName.name === sizeNameInput) && !isEmpty(sizeNameInput);
    const maxSizesReached = sizeNames.length >= MAX_SIZES_COUNT;
    const addElementDisabled = isEmpty(sizeNameInput) ||
        sizeNames.some((sizeName) => sizeName.name === sizeNameInput) ||
        maxSizesReached;
    const handleSeasonNameChange = ({ target: { value } }) => {
        setSizeStandard(value);
        dispatch(updateSizeStandardName({ modelSpecificationSizeStandardId: data === null || data === void 0 ? void 0 : data.id, name: value }));
    };
    const handleSizeChange = (id, value) => {
        setSizeNames((rest) => rest.map((sizeName) => {
            if (sizeName.id === id) {
                const isDuplicated = rest.some((restSizeName) => restSizeName.name === value);
                const isSizeNameEmpty = isEmpty(value);
                let errorMessage = '';
                if (isDuplicated) {
                    errorMessage = t('element_already_exists');
                }
                if (isSizeNameEmpty) {
                    errorMessage = t('element_cannot_be_empty');
                }
                // prioritize empty input error over duplication error
                return {
                    id,
                    name: value,
                    error: isDuplicated || isSizeNameEmpty,
                    errorMessage,
                };
            }
            return sizeName;
        }));
        if (!sizeNames.some((sizeName) => sizeName.name === value) && !isEmpty(value)) {
            dispatch(updateSizeStandardSizeName({ modelSpecificationSizeNameId: id, name: value }));
        }
    };
    const handleSizeRemove = (id) => {
        setSizeNames((rest) => rest.filter((sizeName) => sizeName.id !== id));
        dispatch(removeSizeStandardSizeName(id));
    };
    registerDataValidationErrorMatcher();
    const handleSizeAdd = () => {
        setSizeNameInput('');
        dispatch(addSizeStandardSizeName({ sizeStandardId: data === null || data === void 0 ? void 0 : data.id, name: sizeNameInput }))
            .unwrap()
            .then((sizeName) => {
            setSizeNames((rest) => [...rest, Object.assign(Object.assign({}, sizeName), { error: false, errorMessage: '' })]);
        });
    };
    const handleSizeNameChange = ({ target: { value } }) => {
        setSizeNameInput(value);
    };
    const handleCancel = () => {
        dispatch(resetSizes());
        dispatch(fetchSizes());
    };
    const handleSubmit = () => {
        dispatch(closeModal({ name: 'sizeEditor' }));
        dispatch(resetSizes());
        dispatch(fetchSizes());
    };
    return (React.createElement(Modal, { name: "sizeEditor", size: "s", header: React.createElement(Label, { weight: "strong" }, t('edit_size')), footer: React.createElement(Button, { color: "primary", onClick: handleSubmit }, t('common:done')), onClose: handleCancel },
        React.createElement(Grid, { gap: 24 },
            React.createElement(FormField, { label: t('size_name_capitalized') },
                React.createElement(Input, { value: sizeStandard, name: "name", onChange: handleSeasonNameChange, disabled: !(data === null || data === void 0 ? void 0 : data.canNameBeUpdated) })),
            React.createElement(Grid, null,
                React.createElement(Header, { size: "m" }, t('sizes')),
                sizeNames.map((sizeName) => (React.createElement("div", { key: sizeName.id, style: { display: 'flex', gap: '12px' } },
                    React.createElement(Input, { name: sizeName.id, value: sizeName.name, onChange: () => handleSizeChange(sizeName.id, sizeName.name) }),
                    React.createElement(Button, { theme: "border", shape: "squared", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleSizeRemove(sizeName.id) })))),
                React.createElement(FormField, { label: t('common:name') },
                    React.createElement("div", { style: { display: 'flex', gap: '12px' } },
                        React.createElement(Input, { name: "newSizeName", value: sizeNameInput, disabled: maxSizesReached, onChange: handleSizeNameChange }),
                        React.createElement(Button, { color: "primary", onClick: () => handleSizeAdd(), disabled: addElementDisabled }, t('common:add'))),
                    sizeNameInputError && React.createElement("h5", { className: "errorText" }, t('element_already_exists')),
                    maxSizesReached && React.createElement("h5", null, t('max_sizes_reached')))))));
}
export default memo(SizeEditor);
